import { useEffect } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $dfs, mergeRegister } from '@lexical/utils';
import {
  $createParagraphNode,
  $getNodeByKey,
  $getSelection,
  $isDecoratorNode,
  $isElementNode,
  $isLineBreakNode,
  $isParagraphNode,
  $isRangeSelection,
  $isRootNode,
  $isTextNode,
  ParagraphNode,
} from 'lexical';
import { $isHeadingNode, HeadingNode } from '@lexical/rich-text';
import { ListItemNode } from '@lexical/list';
import { $isImageNode } from '../nodes/ImageNode';

const LEADING_NBSP = /^\u00A0+/;

// Pasted from Google Docs and Word, blocks often open with a line break or a
// run of non-breaking spaces. Neither is ever wanted at the start of a block.
export function $cleanLeadingBreaks(block) {
  const children = block.getChildren();
  if (children.length === 0 || children.every($isLineBreakNode)) return;

  let first = children[0];
  while (first && $isLineBreakNode(first)) {
    const next = first.getNextSibling();
    first.remove();
    first = next;
  }

  if (first && $isTextNode(first) && LEADING_NBSP.test(first.getTextContent())) {
    const trimmed = first.getTextContent().replace(LEADING_NBSP, '');
    if (trimmed === '') {
      first.remove();
    } else {
      first.setTextContent(trimmed);
    }
  }
}

// Images keep Lexical's inline default but the editor places them on their
// own row, so they count as blocks here.
function isBlockChild(node) {
  if ($isImageNode(node)) return true;
  return ($isElementNode(node) || $isDecoratorNode(node)) && !node.isInline();
}

// A paragraph or heading holding a block is no longer a block to Lexical, so
// Enter and paste keep filing new content inside it.
function isEmptyParagraph(node) {
  return (
    $isParagraphNode(node) &&
    node
      .getChildren()
      .every(
        child =>
          $isLineBreakNode(child) || ($isTextNode(child) && child.getTextContent().trim() === ''),
      )
  );
}

function $holdsCaret(node) {
  const selection = $getSelection();
  if (!$isRangeSelection(selection)) return false;
  return [selection.anchor.getNode(), selection.focus.getNode()].some(
    point => point.is(node) || node.isParentOf(point),
  );
}

// Hoisted empty paragraphs wait here until every wrapper is flattened; the
// spacer pass has to count runs over the flat result.
const hoistedByEditor = new WeakMap();

export function $hoistBlockChildren(block, hoisted = new Set()) {
  const children = block.getChildren();
  const firstBlock = children.findIndex(isBlockChild);
  if (firstBlock === -1) return;

  let anchor = block;
  let trailing = null;
  for (const child of children.slice(firstBlock)) {
    if (isBlockChild(child)) {
      anchor.insertAfter(child);
      anchor = child;
      trailing = null;
      if (isEmptyParagraph(child)) hoisted.add(child.getKey());
      continue;
    }
    if (trailing === null) {
      trailing = $createParagraphNode();
      anchor.insertAfter(trailing);
      anchor = trailing;
    }
    trailing.append(child);
  }
  if (trailing !== null && isEmptyParagraph(trailing)) hoisted.add(trailing.getKey());

  if (firstBlock === 0) block.remove();
}

// Nested, an empty paragraph rendered as one line break, so a blank line took
// two. Flat, the block margin already gives one blank line: a run of N keeps
// N-2.
function $collapseHoistedSpacers(hoisted) {
  const parents = new Set();
  for (const key of hoisted) {
    const node = $getNodeByKey(key);
    const parent = node && node.getParent();
    if (parent) parents.add(parent);
  }

  for (const parent of parents) {
    let run = [];
    const flush = () => {
      const deliberate = run.filter(node => !hoisted.has(node.getKey())).length;
      let toRemove = run.length - Math.max(run.length - 2, deliberate);
      for (const node of run) {
        if (toRemove === 0) break;
        if (!hoisted.has(node.getKey()) || $holdsCaret(node)) continue;
        node.remove();
        toRemove -= 1;
      }
      run = [];
    };
    for (const child of parent.getChildren()) {
      if (isEmptyParagraph(child)) run.push(child);
      else flush();
    }
    flush();
  }

  hoisted.clear();
}

function $hasNestedBlocks() {
  return $dfs().some(
    ({ node }) =>
      ($isParagraphNode(node) || $isHeadingNode(node)) && node.getChildren().some(isBlockChild),
  );
}

function $markBlocksDirty() {
  for (const { node } of $dfs()) {
    if ($isElementNode(node) && !$isRootNode(node)) node.markDirty();
  }
}

// Transforms only visit dirty nodes, so a loaded state keeps its nesting until
// every block is touched. Even a no-op update reaches the form as a change,
// hence the guard. The spacer pass needs every wrapper flattened first.
export function healNestedBlocks(editor) {
  if (!editor.getEditorState().read($hasNestedBlocks)) return;
  const hoisted = hoistedByEditor.get(editor) || new Set();
  editor.update($markBlocksDirty, { discrete: true });
  editor.update(() => $collapseHoistedSpacers(hoisted), { discrete: true });
}

// Registering a transform runs it over existing nodes in a history-merge
// tagged update, which the form's change listener ignores, so the healed
// state has to arrive in ordinary updates too.
export function registerParagraphHygiene(editor) {
  const hoisted = new Set();
  hoistedByEditor.set(editor, hoisted);
  const needsHeal = editor.getEditorState().read($hasNestedBlocks);
  const transforms = [ParagraphNode, HeadingNode, ListItemNode]
    .filter(nodeType => editor.hasNodes([nodeType]))
    .map(nodeType => editor.registerNodeTransform(nodeType, $cleanLeadingBreaks));
  const hoists = [ParagraphNode, HeadingNode]
    .filter(nodeType => editor.hasNodes([nodeType]))
    .map(nodeType =>
      editor.registerNodeTransform(nodeType, node => $hoistBlockChildren(node, hoisted)),
    );
  if (needsHeal) {
    Promise.resolve().then(() => {
      editor.update($markBlocksDirty, { discrete: true });
      editor.update(() => $collapseHoistedSpacers(hoisted), { discrete: true });
    });
  }
  return mergeRegister(...transforms, ...hoists);
}

export default function ParagraphHygienePlugin() {
  const [editor] = useLexicalComposerContext();

  useEffect(() => registerParagraphHygiene(editor), [editor]);

  return null;
}
